import type { ContentCard } from '@braze/react-native-sdk'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useEffect, useMemo, useState } from 'react'
import getContentCards from './getContentCards'
import getCachedContentCards from './getCachedContentCards'
import { useLogError } from '@/src/common/hooks/useLogError'

const REFRESH_STORAGE_KEY = 'braze_content_card_last_refresh'
const REFRESH_GLOBAL_RATE_LIMIT = 1000 * 60 * 5 // 5 minutes
const REFRESH_ATTEMPT_INTERVAL = 1000 * 30 // 30 seconds

export function useContentCards(placement: 'home' | 'rewards') {
  const logError = useLogError()
  const [contentCards, setContentCards] = useState<ContentCard[]>([])

  useEffect(() => {
    // Load the cached content cards as soon as we mount the hook
    void getCachedContentCards().then(setContentCards)

    const tryRefreshContentCards = async () => {
      // Why have a global rate limit?
      // Braze limits the number of requests to refresh the content cards to every 180
      // seconds. This hook can be used on multiple screens, so we need to ensure that
      // we don't exceed this limit.
      // Also, this hook can get un/remounted so we need to store the last refresh time
      // in someplace durable.

      try {
        const lastRefreshTime = await AsyncStorage.getItem(REFRESH_STORAGE_KEY)
        const now = Date.now()
        const isRefreshDue = now - parseInt(lastRefreshTime || '0') > REFRESH_GLOBAL_RATE_LIMIT

        if (isRefreshDue) {
          await AsyncStorage.setItem(REFRESH_STORAGE_KEY, now.toString())
          const contentCards = await getContentCards()
          setContentCards(contentCards)
        }
      } catch (err) {
        logError(err)
      }
    }

    // Perform an initial refresh + set up an interval
    void tryRefreshContentCards()

    const refreshInterval = setInterval(() => {
      void tryRefreshContentCards()
    }, REFRESH_ATTEMPT_INTERVAL)

    return () => clearInterval(refreshInterval)
  }, [logError])

  // Filter the content cards to only include the ones for the placement
  // If the placement is not set, assume it's for the home screen.
  const result = useMemo(
    () =>
      contentCards.filter(
        (card) =>
          card.extras.placement === placement || (!card.extras.placement && placement === 'home'),
      ),
    [contentCards, placement],
  )

  return result
}
